import { useQuery } from '@apollo/client'
import { Info } from '@mui/icons-material'
import {
  Alert,
  Box,
  Divider,
  Skeleton,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material'
import { uniq } from 'lodash'
import { useParams } from 'react-router-dom'

import ImageWithBoundingBoxes from '@/components/bounding-box/ImageWithBoundingBoxes'
import { DaiDivider } from '@/components/DaiDivider'
import ProductDetailsCard from '@/components/ProductDetailsCard'
import {
  GET_DISPLAY_SNAPSHOT,
  GetDisplaySnapshotQuery,
  GetDisplaySnapshotQueryVariables,
  ProcessStatus,
} from '@/graphql'
import useT from '@/i18n/useT'
import { getFullSizeImage } from '@/utils/image.helpers'
import { ProductHelpers } from '@/utils/products.helpers'

import PlanogramFromUPCs from '../compliance/PlanogramFromUPCs'

export const EmptyFacingsSnapshot = () => {
  const { t } = useT()
  const { snapshotId } = useParams()
  if (!snapshotId) {
    throw new Error('Snapshot ID is required in the URL')
  }
  const { data, loading } = useQuery<
    GetDisplaySnapshotQuery,
    GetDisplaySnapshotQueryVariables
  >(GET_DISPLAY_SNAPSHOT, { variables: { id: snapshotId } })

  if (loading) {
    return <Skeleton variant="rectangular" width="100%" height={300} />
  }

  if (!data) {
    return <DaiDivider />
  }

  if (data) {
    const emptyProductsUpcs = uniq(
      data.displaySnapshot.emptyFacings.map(p => p.upc).filter(upc => !!upc),
    ) as string[]
    return (
      <Box>
        <DaiDivider />
        <Stack direction="row" spacing={1}>
          <Typography variant="h5" sx={{ mb: 2 }}>
            {t('emptyFacingsSnapshot.productsTitle')}
          </Typography>
          <Tooltip title={t('emptyFacingsSnapshot.productsTooltip')}>
            <Info />
          </Tooltip>
        </Stack>
        <Box
          sx={{
            flex: 1,
            display: 'flex',
            overflowX: 'auto',
            paddingY: 4,
          }}
        >
          <Stack
            direction={'row'}
            spacing={2}
            sx={{
              width: 0 /* Forces the element to not grow beyond parent */,
              minWidth: 0 /* Allows the element to shrink below its content size */,
            }}
          >
            {emptyProductsUpcs.map(upc => (
              <Box
                key={upc}
                sx={{
                  width: 300,
                  display: 'flex',
                  flexShrink: 0, // Prevent shrinking, allowing scroll
                }}
              >
                <ProductDetailsCard
                  upc={upc}
                  key={upc}
                  hideBrandTag
                  hideCategoryTag
                  hideDetails
                  smallProductName
                  hideAlsoKnownAs
                />
              </Box>
            ))}
          </Stack>
          {emptyProductsUpcs.length === 0 && (
            <Typography variant="body1" sx={{ width: '100%' }}>
              {t('emptyFacingsSnapshot.noProducts')}
            </Typography>
          )}
        </Box>
        <Divider sx={{ mb: 3 }} />
        {data.displaySnapshot.images.map(image =>
          image.url ? (
            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              spacing={{ xs: 2, sm: 2 }}
              sx={{
                justifyContent: 'center',
                alignItems: 'center',
              }}
              key={image.id}
            >
              <Stack spacing={2}>
                <Typography variant="h4">{t('common.snapshot')}</Typography>
                <ImageWithBoundingBoxes
                  style={{ width: 350 }}
                  disableHover
                  status={image.process?.status || ProcessStatus.PENDING}
                  errorMessage={image.process?.error}
                  source={getFullSizeImage(image.url)}
                  boxes={ProductHelpers.getEmptyBoxes(
                    image.boxSet?.boundingBoxes || [],
                  ).map(box => ({
                    ...box,
                    color: ProductHelpers.getBoundingBoxColor(
                      box.classifiedLabel,
                    ),
                  }))}
                />
              </Stack>
              {data.displaySnapshot.planogramSegmentShapedFacings.length > 0 ? (
                <Stack spacing={2} sx={{ width: 275 }}>
                  <Typography variant="h4">{t('common.planogram')}</Typography>
                  <PlanogramFromUPCs
                    plan={data.displaySnapshot.planogramSegmentShapedFacings}
                  />
                </Stack>
              ) : (
                <Alert severity="info">
                  {t('emptyFacingsSnapshot.noPlanogramAtTimeOfSnapshot')}
                </Alert>
              )}
            </Stack>
          ) : null,
        )}
      </Box>
    )
  }
}
